import {APIError} from 'data/errors';
import {TypeLocalNameEnum} from 'domain/usecases';
import {PostQuestionsAndAnswersScheduleScriptResponse} from 'domain/usecases/private/booking-flow/health/post-questions-and-answers-schedule-script';
import {
	ChatRoomsEnum,
	HealthBookingEnum,
	HomeEnum,
	QueryParamsEnum,
	TelehealthBookingEnum,
} from 'presentation/constants';
import {FlowEnum} from 'presentation/modules/booking/shared/constants/flowEnum';
import {useBookingFlowStepsStore} from 'presentation/modules/booking/shared/store/useBookingFlowStepsStore';
import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {getStepPosition} from 'presentation/modules/booking/shared/utils/getStepPosition';
import {useBookingSpecialty} from 'presentation/pages/private/booking/hooks';
import {CheckBookingModalEnum} from 'presentation/pages/private/booking/hooks/useBookingSpecialty/types';
import {useGetHealthDistrictsMutation} from 'presentation/pages/private/booking/mutations/useGetHealthDistrictsMutation';
import {
	useAccountInformation,
	useCallChatRoom,
	useQueryParams,
} from 'presentation/pages/private/hooks';
import {translations} from 'presentation/translations-new';
import {formatText, getOnlyFirstWord} from 'presentation/utils';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {BookingTypeEnum, TELEMEDICINE_INFO} from '../../../../constants';
import {useQuestionsAndAnswersScheduleScriptMutation} from '../useQuestionsAndAnswersScheduleScriptMutation';
import {ScriptRedirectToEnum, UseScheduleScriptV2Props} from './types';
import {TelemedicineEnum} from 'presentation/modules/booking/shared/constants/telemedicineEnum';

const {initialChat} = translations['pt-br'].privateFlow.bookingFlow.scheduleScript;

export const useScheduleScriptV2 = ({handleNext}: UseScheduleScriptV2Props) => {
	const containerRef = useRef<HTMLDivElement>(null);
	const hasInitializedRef = useRef(false);
	const requestCountRef = useRef(0);
	const lastProcessedCountRef = useRef(0);

	const [answerOption, setAnswerOption] = useState('Quais são as opções?');
	const [messages, setMessages] = useState<
		PostQuestionsAndAnswersScheduleScriptResponse['content']['messages']
	>([]);
	const [checkedAnswerOptions, setCheckedAnswerOptions] = useState<{idx: number; text: string}[]>(
		[],
	);
	const [openErrorModal, setOpenErrorModal] = useState(false);

	const navigate = useNavigate();
	const {callChatRoom} = useCallChatRoom();
	const setBookingData = useBookingStore(state => state.setBookingData);
	const bookingData = useBookingStore(state => state.bookingData);
	const {setSearchParams} = useQueryParams();
	const setActiveBookingFlowStep = useBookingFlowStepsStore(state => state.setBookingStep);

	const {beneficiary} = useAccountInformation();
	const beneficiaryName = formatText(
		getOnlyFirstWord(beneficiary.nmSocialUsuarioC || beneficiary.nmUsuarioC),
	);
	const greetings = initialChat.hello.replace(':beneficiary', beneficiaryName);

	const {
		data,
		mutate: handleQuestionsAndAnswers,
		isPending,
		isError,
		error,
	} = useQuestionsAndAnswersScheduleScriptMutation();

	const {scheduledBooking, handleCheckDuplicity, checkBookingModal, setCheckBookingModal} =
		useBookingSpecialty({
			flowType:
				bookingData.bookingType === BookingTypeEnum.TELEHEALTH
					? FlowEnum.TELEHEALTH
					: FlowEnum.HEALTH,
			handleNext,
		});

	const resetBookingDataSpecialties = () => {
		setBookingData(prev => ({
			...prev,
			specialtyCode: '',
			subSpecialtyCode: '',
		}));
	};

	const {PROFESSIONAL_STEP} = useMemo(
		() =>
			getStepPosition({
				flowType: FlowEnum.TELEHEALTH,
				isManualLocation: bookingData.isManualLocation,
			}),
		[bookingData.isManualLocation],
	);

	const {DISTRICT_STEP, UNIT_STEP} = useMemo(
		() =>
			getStepPosition({
				flowType: FlowEnum.HEALTH,
				isManualLocation: bookingData.isManualLocation,
			}),
		[bookingData.isManualLocation],
	);

	const getLocationName = useCallback(() => {
		if (bookingData?.isManualLocation) {
			return bookingData?.cityName;
		}
		return bookingData?.coordinates;
	}, [bookingData?.isManualLocation, bookingData?.cityName, bookingData?.coordinates]);

	const getLocationType = useCallback(() => {
		return bookingData?.typeLocalName ?? TypeLocalNameEnum.CIDADE;
	}, [bookingData?.typeLocalName]);

	const getStateCode = useCallback(() => {
		if (bookingData?.isManualLocation) {
			return bookingData?.ufCode;
		}
		return undefined;
	}, [bookingData?.ufCode, bookingData?.isManualLocation]);

	useEffect(() => {
		setSearchParams(
			prev => {
				prev.delete(QueryParamsEnum.SEARCH_BY_LETTER);
				return prev;
			},
			{
				replace: true,
			},
		);
	}, [setSearchParams]);

	useEffect(() => {
		setActiveBookingFlowStep(DISTRICT_STEP);
		if (hasInitializedRef.current) {
			return;
		}
		hasInitializedRef.current = true;
		requestCountRef.current += 1;
		handleQuestionsAndAnswers({
			message: answerOption,
			locationName: getLocationName(),
			locationType: getLocationType(),
			stateCode: getStateCode(),
			restartConversation: true,
		});
	}, []);

	const {mutateAsync: getHealthDistrictsMutation} = useGetHealthDistrictsMutation();

	const handleActionNavigation = useCallback(
		async (actionToPerform: NonNullable<typeof data>['content']['action']) => {
			if (!actionToPerform) return;

			const alreadyScheduled = await handleCheckDuplicity(
				String(actionToPerform.specialtyCode),
			);
			if (alreadyScheduled) {
				if (actionToPerform.telemedicine === TelemedicineEnum.IS_TELEMEDICINE) {
					setBookingData(prev => ({
						...prev,
						bookingType: BookingTypeEnum.TELEHEALTH,
					}));
				}
				setCheckBookingModal(CheckBookingModalEnum.DUPLICITY_STATE);
				return;
			}

			switch (actionToPerform.redirectTo) {
				case ScriptRedirectToEnum.SCHEDULE:
					if (actionToPerform.telemedicine === TelemedicineEnum.IS_TELEMEDICINE) {
						setBookingData(prev => ({
							...prev,
							unitCode: TELEMEDICINE_INFO.code,
							unitName: TELEMEDICINE_INFO.name,
							unitAddress: '',
							ufCode: TELEMEDICINE_INFO.ufCode,
							ufName: TELEMEDICINE_INFO.ufName,
							cityName: TELEMEDICINE_INFO.cityName,
							bookingRedirect: true,
							bookingType: BookingTypeEnum.TELEHEALTH,
							specialtyCode: String(actionToPerform.specialtyCode),
							specialtyName: actionToPerform.specialtyDescription,
							isScheduleScript: false,
						}));
						setActiveBookingFlowStep(PROFESSIONAL_STEP);
						navigate(TelehealthBookingEnum.TELEHEALTH_BOOKING_FLOW, {replace: true});
					} else {
						setBookingData(prev => ({
							...prev,
							specialtyCode: String(actionToPerform.specialtyCode),
							specialtyName: actionToPerform.specialtyDescription,
							hasTelehealth: false,
						}));

						if (bookingData.isManualLocation) {
							try {
								const districts = await getHealthDistrictsMutation({
									stateCode: bookingData.ufCode?.toUpperCase(),
									locationName: bookingData.cityName?.toUpperCase(),
									locationType: TypeLocalNameEnum.CIDADE,
									specialtyCode: actionToPerform.specialtyCode,
								});
								if (!districts || districts.length === 0) {
									setActiveBookingFlowStep(UNIT_STEP);
									setBookingData(prev => ({
										...prev,
										hasTelehealth: true,
										shouldBackToSpecialtyStep: true,
									}));
								} else {
									setActiveBookingFlowStep(DISTRICT_STEP);
								}
							} catch (error) {
								setActiveBookingFlowStep(DISTRICT_STEP);
							}
						} else {
							setActiveBookingFlowStep(UNIT_STEP);
						}

						setBookingData(prev => ({
							...prev,
							isScheduleScript: false,
						}));
						navigate(HealthBookingEnum.HEALTH_BOOKING_FLOW, {replace: true});
					}
					break;
				case ScriptRedirectToEnum.CUSTOMER_CENTER:
					await callChatRoom(ChatRoomsEnum.APP);
					navigate(HomeEnum.HOME_PAGE, {replace: true});
					break;
				default:
					navigate(HomeEnum.HOME_PAGE, {replace: true});
					break;
			}
		},
		[
			navigate,
			callChatRoom,
			setBookingData,
			setActiveBookingFlowStep,
			PROFESSIONAL_STEP,
			DISTRICT_STEP,
			UNIT_STEP,
			bookingData.isManualLocation,
		],
	);

	const shouldVerifyDistrictsBeforeSkip =
		data?.content?.action?.redirectTo === ScriptRedirectToEnum.SCHEDULE &&
		data?.content?.action?.telemedicine !== TelemedicineEnum.IS_TELEMEDICINE &&
		bookingData.isManualLocation;

	useEffect(() => {
		if (!data) return;
		if (lastProcessedCountRef.current === requestCountRef.current) return;
		lastProcessedCountRef.current = requestCountRef.current;

		setMessages(prev =>
			prev.length === 0 ? data.content.messages : [...prev, ...data.content.messages],
		);

		if (data.content?.finalized && data.content?.action) {
			setTimeout(
				() => {
					handleActionNavigation(data.content.action);
				},
				shouldVerifyDistrictsBeforeSkip ? 1000 : 1500,
			);
		}
	}, [data, handleActionNavigation, shouldVerifyDistrictsBeforeSkip]);

	useEffect(() => {
		if (containerRef.current) {
			containerRef.current.scrollTo({
				top: containerRef.current.scrollHeight,
				behavior: 'smooth',
			});
		}
	}, [messages, checkedAnswerOptions, isPending]);

	useEffect(() => {
		if (isError && (error as APIError).statusCode === 500) {
			setOpenErrorModal(true);
		}
	}, [isError, error]);

	const handleCloseErrorModal = useCallback(() => {
		setOpenErrorModal(false);
	}, []);

	const handleChangeMessage = useCallback(
		(option: string) => {
			const currentMessageIndex = messages.length - 1;
			setAnswerOption(option);
			setCheckedAnswerOptions(prev => [...prev, {idx: currentMessageIndex, text: option}]);
			requestCountRef.current += 1;
			handleQuestionsAndAnswers({
				message: option,
				locationName: getLocationName(),
				locationType: getLocationType(),
				stateCode: getStateCode(),
				restartConversation: false,
			});
		},
		[
			handleQuestionsAndAnswers,
			messages.length,
			getLocationName,
			getLocationType,
			getStateCode,
		],
	);

	const isActionButton = useCallback((answerOptions?: string[]) => {
		if (!answerOptions) return false;
		return (
			answerOptions.length === 2 &&
			answerOptions
				?.map(option => option.toLowerCase())
				?.includes('falar com atendimento online')
		);
	}, []);

	const handleRetryRequest = useCallback(() => {
		handleQuestionsAndAnswers({
			message: answerOption,
			locationName: getLocationName(),
			locationType: getLocationType(),
			stateCode: getStateCode(),
			restartConversation: false,
		});
	}, [handleQuestionsAndAnswers, answerOption, getLocationName, getLocationType, getStateCode]);

	const handleRestartConversation = useCallback(() => {
		handleQuestionsAndAnswers({
			message: 'Quais são as opções?',
			locationName: getLocationName(),
			locationType: getLocationType(),
			stateCode: getStateCode(),
			restartConversation: true,
		});
	}, [handleQuestionsAndAnswers, getLocationName, getLocationType, getStateCode]);

	return {
		containerRef,
		messages,
		checkedAnswerOptions,
		openErrorModal,
		isPending,
		isError,
		error,
		greetings,
		isActionButton,
		handleCloseErrorModal,
		handleChangeMessage,
		handleRetryRequest,
		handleRestartConversation,
		scheduledBooking,
		checkBookingModal,
		resetBookingDataSpecialties,
		setCheckBookingModal,
	};
};
